import { inject, Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { ModalService } from '@src/suite/common/components/modal/modal.service';
import { GlobalTablesService } from '@src/suite/common/services/global-tables-service';
import { IndexedDBService } from '@src/suite/common/services/indexeddb-service';
import { NavigationService } from '@src/suite/common/services/navigation-service';
import { NomWebServicesJson } from '@src/suite/common/services/nom-web-services-json';
import { GlobalTablesActions } from '@src/suite/common/store/global-tables/global-tables.actions';
import { ApplicationActions } from '@src/suite/common/store/navigation/actions/application.actions';
import { NavigationActions } from '@src/suite/common/store/navigation/actions/navigation.actions';
import { exhaustMap, forkJoin, map, mergeMap, take } from 'rxjs';

import { selectJWTToken } from '../selectors/application.selectors';

@Injectable()
export class ApplicationEffects {
    private actions$ = inject(Actions);
    private store = inject(Store);
    router = inject(Router);
    modalService = inject(ModalService);
    private snackBar = inject(MatSnackBar);

    private indexedDBService = inject(IndexedDBService);
    private navigationService = inject(NavigationService);
    private globalTablesService = inject(GlobalTablesService);
    private nomWebServicesJson = inject(NomWebServicesJson);

    setJWTToken$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(ApplicationActions.setJWTToken),
                concatLatestFrom(() => [this.store.select(selectJWTToken)]),
                map(([_, jwtToken]) => {
                    this.nomWebServicesJson?.imgServiceWorker?.postMessage({ authorization: jwtToken });
                    localStorage.setItem('jwtToken', jwtToken);
                    if (window?.['webkit']?.messageHandlers?.interOp?.postMessage) {
                        window['webkit'].messageHandlers?.interOp?.postMessage({ name: 'setJWT', jwtToken: jwtToken });
                    }
                })
            );
        },
        { dispatch: false }
    );

    removeJWTToken$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(ApplicationActions.removeJWTToken),
                map(() => {
                    localStorage.removeItem('jwtToken');
                    if (window?.['webkit']?.messageHandlers?.interOp?.postMessage) {
                        window['webkit'].messageHandlers?.interOp?.postMessage({ name: 'setJWT', jwtToken: null });
                    }
                })
            );
        },
        { dispatch: false }
    );

    sessionInitialized$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ApplicationActions.sessionInitialized),
            exhaustMap(() => {
                return forkJoin({ db: this.indexedDBService.connect(), navigation: this.navigationService.loadNavigation(), globaltables: this.globalTablesService.getGlobalTablesSimple() }).pipe(
                    mergeMap(res => {
                        return [NavigationActions.loadNavigation({ navigation: res.navigation }), GlobalTablesActions.setGlobalTables(res.globaltables)];
                    })
                );
            })
        );
    });

    // @TODO Should be fixed after routing refactor
    checkHasSession$ = createEffect(() => {
        return this.actions$.pipe(
            ofType(ApplicationActions.checkHasSession),
            take(1),
            exhaustMap(() => {
                return forkJoin({ db: this.indexedDBService.connect(), navigation: this.navigationService.loadNavigation(), globaltables: this.globalTablesService.getGlobalTablesSimple() }).pipe(
                    mergeMap(res => {
                        return [NavigationActions.loadNavigation({ navigation: res.navigation }), GlobalTablesActions.setGlobalTables(res.globaltables)];
                    })
                );
            })
        );
    });

    showAlert$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(ApplicationActions.showAlertMessage),
                map(action => {
                    this.modalService.openAlert({
                        title: action.title,
                        content: action.message,
                        severity: action.severity,
                    });
                })
            );
        },
        { dispatch: false }
    );

    showToast$ = createEffect(
        () => {
            return this.actions$.pipe(
                ofType(ApplicationActions.showToastMessage),
                map(action => {
                    this.snackBar.open(action.message, null, {
                        duration: 5000,
                        panelClass: action.severity ? [`snackbar--${action.severity}`] : [],
                    });
                })
            );
        },
        { dispatch: false }
    );
}
